import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";
import Loading from "../components/Loading";
import ErrorBoundary from "../components/ErrorBoundary";
import RotaProtegidaPorPermissao from "../components/RotaProtegidaPorPermissao";

// --- Componente que bloqueia rota no multi-tenant ---
const BlockInMultiTenant: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.unidade) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
};

// --- Componente que exige multi-tenant (qualquer filial, menos central) ---
// Inverso de BlockInMultiTenant: usado por telas que só fazem sentido logado
// numa filial (ex.: Usuários da Filial, escrita unidade -> central).
const RequireMultiTenant: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  if (!user?.unidade || user.unidade === "central") {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
};

// --- Componente que libera rota apenas pra filial específica ---
// Uso: módulos exclusivos da filial CenterSport (ex.: Romaneio de Frete)
const OnlyForUnidade: React.FC<{ children: React.ReactNode; unidade: string | string[] }> = ({
  children,
  unidade,
}) => {
  const { user } = useAuth();
  const unidadesPermitidas = Array.isArray(unidade) ? unidade : [unidade];
  if (!user?.unidade || !unidadesPermitidas.includes(user.unidade)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
};

// --- Componente para rotas protegidas ---
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Loading message="Verificando autenticação..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// --- Portal externo: rota protegida (só parceiro logado por CNPJ) ---
const PortalProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return <Loading message="Verificando autenticação..." />;
  }
  if (!isAuthenticated || user?.tipo !== "externo") {
    return <Navigate to="/portal/login" replace />;
  }
  return <>{children}</>;
};

// --- Portal externo: rota pública (redireciona parceiro já logado) ---
const PortalPublicRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { loading, user } = useAuth();
  if (loading) {
    return <Loading message="Verificando autenticação..." />;
  }
  if (user?.tipo === "externo") {
    return <Navigate to="/portal/agendamentos" replace />;
  }
  return <>{children}</>;
};

// --- Componente para rotas públicas ---
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <Loading message="Verificando autenticação..." />;
  }

  if (isAuthenticated) {
    if (user?.unidade === "central") {
      return <Navigate to="/admin-central" replace />;
    }
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

// --- WMS: Lazy-loaded pages ---
const Login = React.lazy(() => import("../features/geral/auth/pages/LoginPage"));
const ConfigurarSenha = React.lazy(() => import("../features/geral/auth/pages/ConfigurarSenhaPage"));
const Home = React.lazy(() => import("../features/wms/home/pages/HomePage"));
const DashboardEstoque = React.lazy(() => import("../features/wms/dashboard/pages/DashboardEstoquePage"));
const DashboardNotas = React.lazy(() => import("../features/wms/nota-fiscal/dashboard/pages/DashboardNotasPage"));
const DashboardReposicao = React.lazy(() => import("../features/oms/reposicoes/dashboard/pages/DashboardReposicaoPage"));
const DashboardPedidos = React.lazy(() => import("../features/oms/pedidos/dashboard/pages/DashboardPedidosPage"));
const DashboardFull = React.lazy(() => import("../features/oms/full/dashboard/pages/DashboardFullPage"));
// --- Armazem: migrado para features/wms/armazem/ ---
const Armazem = React.lazy(() => import("../features/wms/armazem/pages/ArmazemListPage"));
const CriarArmazem = React.lazy(() => import("../features/wms/armazem/pages/CriarArmazemPage"));
const EditarArmazem = React.lazy(() => import("../features/wms/armazem/pages/EditarArmazemPage"));
const ConfiguracaoArmazem = React.lazy(() => import("../features/wms/armazem/pages/ConfiguracaoArmazemPage"));
// --- Localizacao: migrado para features/wms/localizacao/ ---
const Localizacao = React.lazy(() => import("../features/wms/localizacao/pages/LocalizacaoListPage"));
const CriarLocalizacao = React.lazy(() => import("../features/wms/localizacao/pages/CriarLocalizacaoPage"));
const EditarLocalizacao = React.lazy(() => import("../features/wms/localizacao/pages/EditarLocalizacaoPage"));
const TipoLocalizacao = React.lazy(() => import("../features/wms/localizacao/pages/TipoLocalizacaoListPage"));
const CriarTipoLocalizacao = React.lazy(() => import("../features/wms/localizacao/pages/CriarTipoLocalizacaoPage"));
const EditarTipoLocalizacao = React.lazy(() => import("../features/wms/localizacao/pages/EditarTipoLocalizacaoPage"));
// --- Produto: migrado para features/wms/produto/ ---
const Produto = React.lazy(() => import("../features/wms/produto/pages/ProdutoListPage"));
const CriarProduto = React.lazy(() => import("../features/wms/produto/pages/CriarProdutoPage"));
const EditarProduto = React.lazy(() => import("../features/wms/produto/pages/EditarProdutoPage"));
const GestaoProdutos = React.lazy(() => import("../features/wms/produto/pages/GestaoProdutosPage"));
const Consulta = React.lazy(() => import("../features/wms/movimentacao/pages/ConsultaEstoquePage"));
const Movimentacao = React.lazy(() => import("../features/wms/movimentacao/pages/MovimentacaoPage"));
const HistoricoMovimentacao = React.lazy(() => import("../features/wms/movimentacao/pages/HistoricoMovimentacaoPage"));
const Separacao = React.lazy(() => import("../features/wms/separacao/pages/SeparacaoPage"));
const Ocorrencias = React.lazy(() => import("../features/wms/ocorrencia/pages/OcorrenciaListPage"));
const NovaOcorrencia = React.lazy(() => import("../features/wms/ocorrencia/pages/NovaOcorrenciaPage"));
const RelatorioOcorrencias = React.lazy(() => import("../features/geral/relatorios/components/wms/RelatorioOcorrenciasConteudo"));
const Usuarios = React.lazy(() => import("../features/geral/usuario/pages/UsuarioListPage"));
const UsuariosFilial = React.lazy(() => import("../features/geral/usuarios-filial/pages/UsuariosFilialPage"));
const CriarUsuario = React.lazy(() => import("../features/geral/usuario/pages/CriarUsuarioPage"));
const PerfilUsuario = React.lazy(() => import("../features/geral/perfil-permissao/pages/PerfilUsuarioListPage"));
const CriarPerfilUsuario = React.lazy(() => import("../features/geral/perfil-permissao/pages/CriarPerfilUsuarioPage"));
const VisualizarPerfilUsuario = React.lazy(() => import("../features/geral/perfil-permissao/pages/VisualizarPerfilUsuarioPage"));
const PermissoesExtras = React.lazy(() => import("../features/geral/perfil-permissao/pages/PermissoesExtrasPage"));
const Parametros = React.lazy(() => import("../features/geral/parametros/pages/ParametrosPage"));
const MovimentacaoMobile = React.lazy(() => import("../features/geral/movimentacao/pages/MovimentacaoPage"));
const ConferenciaLocalizacao = React.lazy(() => import("../features/wms/conferencia-loc/pages/ConferenciaLocalizacaoPage"));
const Balanco = React.lazy(() => import("../features/wms/balanco/pages/BalancoListPage"));
const DetalhesBalanco = React.lazy(() => import("../features/wms/balanco/pages/DetalhesBalancoPage"));
const TransferenciasBalanco = React.lazy(() => import("../features/wms/balanco/pages/TransferenciasBalancoPage"));
const InventarioList = React.lazy(() => import("../features/wms/inventario/pages/InventarioListPage"));
const CriarInventario = React.lazy(() => import("../features/wms/inventario/pages/CriarInventarioPage"));
const OperarInventario = React.lazy(() => import("../features/wms/inventario/pages/OperarInventarioPage"));
const DashboardInventario = React.lazy(() => import("../features/wms/inventario/pages/InventarioDashboardPage"));
const EncerramentoInventario = React.lazy(() => import("../features/wms/inventario/pages/EncerramentoInventarioPage"));
const RelatorioInventarioImpressao = React.lazy(() => import("../features/wms/inventario/pages/RelatorioInventarioImpressaoPage"));
const NotasFiscais = React.lazy(() => import("../features/wms/nota-fiscal/pages/NotasFiscaisListPage"));
const DetalhesNotaFiscal = React.lazy(() => import("../features/wms/nota-fiscal/pages/DetalhesNotaFiscalPage"));
const AgendamentosEntrega = React.lazy(() => import("../features/wms/agendamento-entrega/pages/AgendamentoEntregaPage"));
const RelatorioTransferencias = React.lazy(() => import("../features/wms/agendamento-entrega/pages/RelatorioTransferenciasPage"));
const PortalLogin = React.lazy(() => import("../features/portal/auth/pages/PortalLoginPage"));
const PortalCadastro = React.lazy(() => import("../features/portal/auth/pages/PortalCadastroPage"));
const PortalConfigurarSenha = React.lazy(() => import("../features/portal/auth/pages/PortalConfigurarSenhaPage"));
const PortalEsqueciSenha = React.lazy(() => import("../features/portal/auth/pages/PortalEsqueciSenhaPage"));
const ConferenciaCompras = React.lazy(() => import("../features/wms/conferencia-compras/pages/ConferenciaComprasListPage"));
const DetalhesConferenciaCompras = React.lazy(() => import("../features/wms/conferencia-compras/pages/DetalhesConferenciaComprasPage"));
const VolumesConferenciaCompras = React.lazy(() => import("../features/wms/conferencia-compras/pages/VolumesConferenciaComprasPage"));
const ConferenciaWebBipagem = React.lazy(() => import("../features/wms/conferencia-compras/pages/ConferenciaWebBipagemPage"));
const OcorrenciasNF = React.lazy(() => import("../features/wms/conferencia-compras/pages/OcorrenciasNFPage"));
const ConferenciaDevolucao = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/ConferenciaDevolucaoListPage"));
const DetalhesConferenciaDevolucao = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/DetalhesConferenciaDevolucaoPage"));
const ConferenciaDevolucaoPage = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/ConferenciaDevolucaoPage"));
const OcorrenciasDevolucao = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/OcorrenciasDevolucaoListPage"));
const RomaneioColeta = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/RomaneioColetaPage"));
const RelatorioDevolucao = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/RelatorioDevolucaoPage"));
const PendenciaMesa = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/PendenciaMesaPage"));
const RelatorioSobraMesa = React.lazy(() => import("../features/wms/conferencia-devolucao/pages/RelatorioSobraMesaPage"));

// --- OMS: Lazy-loaded pages ---
const OMSDashboard = React.lazy(() => import("../features/oms/dashboard/pages/OMSDashboardPage"));
const Pedidos = React.lazy(() => import("../features/oms/pedidos/pages/PedidosPage"));
const Embalagem = React.lazy(() => import("../features/oms/embalagem/pages/EmbalagemPage"));
const Tarefas = React.lazy(() => import("../features/oms/tarefas/pages/TarefasPage"));
const Trocas = React.lazy(() => import("../features/oms/trocas/pages/TrocasPage"));
const Full = React.lazy(() => import("../features/oms/full/pages/FullPage"));
const ProdutosFull = React.lazy(() => import("../features/oms/produtos-full/pages/ProdutosFullPage"));
const RastreioFull = React.lazy(() => import("../features/oms/full/pages/RastreioFullPage"));
const Envios = React.lazy(() => import("../features/oms/envios/pages/EnviosPage"));
const RelatorioColetaFull = React.lazy(() => import("../features/geral/relatorios/pages/RelatorioColetaFullPage"));
const ConferenciaFull = React.lazy(() => import("../features/oms/conferencia-full/pages/ConferenciaFullPage"));
const ConferenciaFullBipagem = React.lazy(() => import("../features/oms/conferencia-full/pages/ConferenciaFullBipagemPage"));
const DetalhesConferenciaFull = React.lazy(() => import("../features/oms/conferencia-full/pages/DetalhesConferenciaFullPage"));
const MesasSeparacao = React.lazy(() => import("../features/oms/config/pages/MesasSeparacaoPage"));
const HorariosSeparadores = React.lazy(() => import("../features/oms/config/pages/HorariosSeparadoresPage"));
const CanaisVenda = React.lazy(() => import("../features/oms/config/pages/CanaisVendaPage"));
const RelatorioAbaixoMinimo = React.lazy(() => import("../features/oms/reposicoes/pages/RelatorioAbaixoMinimoPage"));
const ReposicaoDiferenca = React.lazy(() => import("../features/oms/reposicoes/pages/ReposicaoDiferencaPage"));
const TransferenciaNova = React.lazy(() => import("../features/oms/reposicoes/pages/TransferenciaNovaPage"));
const TransferenciasList = React.lazy(() => import("../features/oms/reposicoes/pages/TransferenciasListPage"));
const ReposicaoExecucao = React.lazy(() => import("../features/oms/reposicoes/execucao/pages/ReposicaoExecucaoPage"));
const ReposicaoPedido = React.lazy(async () => {
  const m = await import("../features/oms/reposicoes/pages/ReposicoesPage");
  return { default: () => <m.default escopo="pedido" /> };
});
const ColetaReposicao = React.lazy(() => import("../features/oms/coleta-reposicao/pages/ColetaReposicaoPage"));
const Romaneio = React.lazy(() => import("../features/oms/romaneio/pages/RomaneioPage"));
// --- Geral: Lazy-loaded pages ---
const RelatorioGeral = React.lazy(() => import("../features/geral/relatorios/pages/RelatorioGeralPage"));

// --- Admin Central ---
const AdminCentral = React.lazy(() => import("../features/admin-central/pages/AdminCentralPage"));
const AdminUnidades = React.lazy(() => import("../features/admin-central/pages/UnidadesPage"));
const AdminUsuarios = React.lazy(() => import("../features/admin-central/pages/UsuariosPage"));
const AdminParceiros = React.lazy(() => import("../features/admin-central/pages/ParceirosPage"));
const AdminIntegracoes = React.lazy(() => import("../features/admin-central/pages/IntegracoesPage"));
const AdminPerfis = React.lazy(() => import("../features/admin-central/pages/PerfisPage"));

const AppRoutes: React.FC = () => {
  usePageTitle();

  return (
  <Suspense fallback={<Loading message="Carregando página..." />}>
    <ErrorBoundary>
    <Routes>
      {/* Login sem menu lateral */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* Configurar senha - rota pública para primeiro acesso */}
      <Route path="/configurar-senha" element={<ConfigurarSenha />} />

      {/* #1694: dashboard do módulo de estoque. Mesma permissão do painel
          geral — é a que já separa quem vê tela analítica de quem não vê. */}
      <Route
        path="/estoque/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="dashboard">
              <DashboardEstoque />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* #1694: dashboard do módulo de notas fiscais. */}
      <Route
        path="/notas-fiscais/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="dashboard">
              <DashboardNotas />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* #1694: dashboard do módulo de reposição. */}
      <Route
        path="/reposicoes/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="dashboard">
              <DashboardReposicao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* #1694: dashboard do módulo de Full. */}
      <Route
        path="/full/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="dashboard">
              <DashboardFull />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* #1694: dashboard do módulo de pedidos. */}
      <Route
        path="/pedidos/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="dashboard">
              <DashboardPedidos />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Páginas de localização */}
      <Route
        path="/localizacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="localizacao">
              <Localizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/localizacao/criar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="localizacao">
              <CriarLocalizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/localizacao/:id/editar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="localizacao">
              <EditarLocalizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Páginas de Armazém */}
      <Route
        path="/armazem"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="armazem">
              <Armazem />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/criar-armazem"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="armazem">
              <CriarArmazem />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/armazem/:id/editar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="armazem">
              <EditarArmazem />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/armazem/configuracao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="armazem">
              <ConfiguracaoArmazem />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Páginas de Tipo de localização */}
      <Route
        path="/tipo-localizacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="tipo_localizacao">
              <TipoLocalizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tipo-localizacao/:id/editar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="tipo_localizacao">
              <EditarTipoLocalizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tipo-localizacao/criar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="tipo_localizacao">
              <CriarTipoLocalizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Páginas de Produto */}
      <Route
        path="/produto"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="produto">
              <Produto />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/produto/criar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="produto">
              <CriarProduto />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/produto/:id/editar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="produto">
              <EditarProduto />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/produto/gestao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="produto">
              <GestaoProdutos />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        }
      />
      <Route
        path="/consulta"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="consulta">
              <Consulta />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/movimentacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="movimentacao">
              <Movimentacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/separacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="separacao">
              <Separacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ocorrencia"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="ocorrencia">
              <Ocorrencias />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/relatorio"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="relatorio">
              <Navigate to="/geral/relatorio?aba=wms" replace />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/relatorio/ocorrencias"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="relatorio">
              <RelatorioOcorrencias />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/historico-movimentacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="consulta">
              <HistoricoMovimentacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/criar-ocorrencia"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="ocorrencia">
              <NovaOcorrencia />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-localizacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="conferencia_localizacao">
              <ConferenciaLocalizacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      <Route
        path="/balanco"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="balanco">
              <Balanco />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/balanco/:id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="balanco">
              <DetalhesBalanco />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/balanco/:id/transferencias"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="balanco">
              <TransferenciasBalanco />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Inventário (módulo novo, free-roaming). #1862: módulo próprio
          `inventario` — antes só exigia login. */}
      <Route
        path="/wms/inventario"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="inventario">
              <InventarioList />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/inventario/criar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="inventario">
              <CriarInventario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/inventario/:id/operar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="inventario">
              <OperarInventario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/inventario/:id/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="inventario">
              <DashboardInventario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/inventario/:id/encerramento"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="inventario">
              <EncerramentoInventario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      {/* Relatório dedicado para impressão — sem Layout/Sidebar, abre em
          nova aba e dispara window.print() automaticamente. */}
      <Route
        path="/wms/inventario/:id/relatorio-impressao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="inventario">
              <RelatorioInventarioImpressao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Notas Fiscais */}
      <Route
        path="/notas-fiscais"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <NotasFiscais />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/notas-fiscais/:id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <DetalhesNotaFiscal />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Agendamento de Entrega — uma tela; o modo (Consulta/Transportadora) é por permissão */}
      <Route
        path="/agendamentos-entrega"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo={["agendamento_entrega", "agendamento_entrega_consulta"]}>
              <AgendamentosEntrega />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* #1426 — Relatório de transferências recebidas (ex-"agendamentos passados"):
          saiu do modal e virou tela, mesma permissão do agendamento */}
      <Route
        path="/agendamentos-entrega/transferencias"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo={["agendamento_entrega", "agendamento_entrega_consulta"]}>
              <RelatorioTransferencias />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Conferência de Compras */}
      <Route
        path="/conferencia-compras"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="conferencia_nota">
              <ConferenciaCompras />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-compras/:id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="conferencia_nota">
              <DetalhesConferenciaCompras />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-compras/:id/volumes"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="conferencia_nota">
              <VolumesConferenciaCompras />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-compras/:id/conferir"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="conferencia_nota">
              <ConferenciaWebBipagem />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Ocorrências NF */}
      <Route
        path="/ocorrencias-nf"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="ocorrencia_nf">
              <OcorrenciasNF />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Conferência Devolução */}
      <Route
        path="/conferencia-devolucao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <ConferenciaDevolucao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      {/* #1965 — a tela Full virou aba. O link antigo continua valendo e cai
          na aba certa, para favorito e link colado em conversa não quebrarem. */}
      <Route
        path="/devolucoes-full"
        element={<Navigate to="/conferencia-devolucao?origem=FULL" replace />}
      />
      <Route
        path="/conferencia-devolucao/ocorrencias"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <OcorrenciasDevolucao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-devolucao/romaneio-coleta"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <RomaneioColeta />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-devolucao/relatorio"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <RelatorioDevolucao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      {/* Pendência da mesa de separação (#1768) — sobra sem dono na mesa. */}
      <Route
        path="/conferencia-devolucao/mesa"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <PendenciaMesa />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-devolucao/mesa/relatorio"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <RelatorioSobraMesa />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-devolucao/nota/:id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <DetalhesConferenciaDevolucao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/conferencia-devolucao/conferencia/:id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="nota_fiscal">
              <ConferenciaDevolucaoPage />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Usuarios — bloqueado no multi-tenant (gerenciado pelo central) */}
      <Route
        path="/usuario"
        element={
          <ProtectedRoute>
            <BlockInMultiTenant>
              <RotaProtegidaPorPermissao modulo="usuario">
                <Usuarios />
              </RotaProtegidaPorPermissao>
            </BlockInMultiTenant>
          </ProtectedRoute>
        }
      />

      {/* Usuários da Filial — só multi-tenant, escrita unidade -> central */}
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute>
            <RequireMultiTenant>
              <RotaProtegidaPorPermissao modulo="usuario_filial">
                <UsuariosFilial />
              </RotaProtegidaPorPermissao>
            </RequireMultiTenant>
          </ProtectedRoute>
        }
      />
      <Route
        path="/criar-usuario"
        element={
          <ProtectedRoute>
            <BlockInMultiTenant>
              <RotaProtegidaPorPermissao modulo="usuario">
                <CriarUsuario />
              </RotaProtegidaPorPermissao>
            </BlockInMultiTenant>
          </ProtectedRoute>
        }
      />

      {/* Parâmetros Configuráveis */}
      <Route path="/parametros" element={
        <ProtectedRoute>
          <RotaProtegidaPorPermissao modulo="perfil">
            <Parametros />
          </RotaProtegidaPorPermissao>
        </ProtectedRoute>
      } />

      {/* Movimentação (controle de visibilidade no mobile) */}
      <Route path="/movimentacao-mobile" element={
        <ProtectedRoute>
          <RotaProtegidaPorPermissao modulo="movimentacao_mobile">
            <MovimentacaoMobile />
          </RotaProtegidaPorPermissao>
        </ProtectedRoute>
      } />

      {/* Geral - Relatórios unificados (WMS/OMS) */}
      <Route
        path="/geral/relatorio"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao
              modulo={["relatorio", "oms_relatorios"]}
            >
              <RelatorioGeral />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Perfis de Usuário */}
      <Route
        path="/perfil-usuario"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="perfil">
              <PerfilUsuario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/perfil-usuario/criar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="perfil">
              <CriarPerfilUsuario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/perfil-usuario/editar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="perfil">
              <CriarPerfilUsuario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/perfil-usuario/visualizar"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="perfil">
              <VisualizarPerfilUsuario />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />

      {/* Permissões Extras */}
      <Route
        path="/permissoes-extras"
        element={
          <ProtectedRoute>
            <PermissoesExtras />
          </ProtectedRoute>
        }
      />

      {/* Rotas do OMS */}
      <Route
        path="/oms/dashboard"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_dashboard">
              <OMSDashboard />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/tarefas"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_tarefas">
              <Tarefas />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/trocas"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_trocas">
              <Trocas />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/full"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_full">
              <Full />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/full/:fullId/produtos"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_full">
              <ProdutosFull />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/full/:fullId/rastreio"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_full">
              <RastreioFull />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/envios"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_envio">
              <Envios />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/relatorio-coleta-full"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_coleta_full">
              <RelatorioColetaFull />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/conferencia-full"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_conferencia_full">
              <ConferenciaFull />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/conferencia-full/:id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_conferencia_full">
              <DetalhesConferenciaFull />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/conferencia-full/:id/bipagem"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_conferencia_full">
              <ConferenciaFullBipagem />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/reposicao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_reposicao">
              <ReposicaoPedido />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/reposicao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_reposicao">
              <TransferenciasList />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/reposicao/relatorio-abaixo-minimo"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_reposicao">
              <RelatorioAbaixoMinimo />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/reposicao/diferenca"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_reposicao">
              <ReposicaoDiferenca />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/reposicao/nova"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_reposicao">
              <TransferenciaNova />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/wms/reposicao/executar/:tarefa_id"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_reposicao">
              <ReposicaoExecucao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/coleta-reposicao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_coleta_reposicao">
              <ErrorBoundary>
                <ColetaReposicao />
              </ErrorBoundary>
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/pedidos"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_pedidos">
              <Pedidos />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/embalagem"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_embalagem">
              <ErrorBoundary>
                <Embalagem />
              </ErrorBoundary>
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/romaneio"
        element={
          <ProtectedRoute>
            <OnlyForUnidade unidade={["center", "minas"]}>
              <RotaProtegidaPorPermissao modulo="oms_romaneio">
                <ErrorBoundary>
                  <Romaneio />
                </ErrorBoundary>
              </RotaProtegidaPorPermissao>
            </OnlyForUnidade>
          </ProtectedRoute>
        }
      />
      <Route
        path="/romaneio-preview"
        element={
          <ErrorBoundary>
            <Romaneio standalone />
          </ErrorBoundary>
        }
      />
      <Route
        path="/oms/mesas-separacao"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_mesas_separacao">
              <MesasSeparacao />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/horarios-separadores"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_horarios_separadores">
              <HorariosSeparadores />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/canais-venda"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_canal_venda">
              <CanaisVenda />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oms/relatorios"
        element={
          <ProtectedRoute>
            <RotaProtegidaPorPermissao modulo="oms_relatorios">
              <Navigate to="/geral/relatorio?aba=oms" replace />
            </RotaProtegidaPorPermissao>
          </ProtectedRoute>
        }
      />
      {/* Admin Central */}
      <Route
        path="/admin-central"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminCentral />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-central/unidades"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminUnidades />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-central/usuarios"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminUsuarios />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-central/parceiros"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminParceiros />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-central/integracoes/:slug"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminIntegracoes />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-central/perfis/:slug"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <AdminPerfis />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Redirecionamentos */}
      {/* Portal externo (parceiro por CNPJ) — layout restrito, sem Sidebar */}
      <Route
        path="/portal/login"
        element={
          <PortalPublicRoute>
            <PortalLogin />
          </PortalPublicRoute>
        }
      />
      <Route path="/portal/cadastro" element={<PortalCadastro />} />
      <Route path="/portal/configurar-senha" element={<PortalConfigurarSenha />} />
      <Route path="/portal/esqueci-senha" element={<PortalEsqueciSenha />} />
      <Route
        path="/portal/agendamentos"
        element={
          <PortalProtectedRoute>
            <AgendamentosEntrega />
          </PortalProtectedRoute>
        }
      />

      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </ErrorBoundary>
  </Suspense>
  );
};

export default AppRoutes;
